/**
 * tripApi.ts — Flask-backed trip & saved-campsite API for the website.
 *
 * Uses the user JWT (stored as 'userToken' in localStorage) which the Flask
 * backend's /api/trips and /api/auth/saved-campsites endpoints accept.
 *
 * All functions are no-ops / return empty results when the user is not logged in,
 * so callers can fall back to localStorage transparently.
 */

import { getUserToken } from './reviewerApi';

const API_BASE = (process.env.NEXT_PUBLIC_API_URL || 'https://app.trip-planner.com.au') + '/api';

function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return getUserToken();
}

function authHeaders(): HeadersInit {
  const token = getToken();
  return token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' };
}

export function isLoggedIn(): boolean {
  return !!getToken();
}

// ─── Trip types (matches Flask unified model) ─────────────────────────────────

export interface ApiTripStop {
  id: string;
  entity_id: number | null;
  name: string;
  kind: string;           // 'start' | 'end' | 'via' | 'overnight' | 'camp' (+ legacy 'waypoint' | 'poi')
  lat?: number | null;
  lng?: number | null;
  nights: number;
  notes?: string | null;
  order_index: number;
  locked?: boolean;
  tags?: string[];
  constraints?: string[];
  added_at: string;
}

export interface ApiTrip {
  id: string;
  name: string;
  start_date?: string | null;
  end_date?: string | null;
  notes?: string | null;
  /** Daily driving budget the planner splits the trip by. */
  drive_hours_per_day?: number | null;
  /** 'car' | '4wd' | 'caravan' | 'motorhome' … — shapes routing + camp picks. */
  vehicle_type?: string | null;
  created_at: string;
  updated_at: string;
  stops: ApiTripStop[];
}

/** Optional trip-level fields accepted by POST/PUT /api/trips. */
export interface ApiTripOptions {
  start_date?: string | null;
  end_date?: string | null;
  notes?: string;
  drive_hours_per_day?: number | null;
  vehicle_type?: string | null;
}

// ─── Trips ─────────────────────────────────────────────────────────────────────

export async function apiListTrips(): Promise<ApiTrip[]> {
  if (!isLoggedIn()) return [];
  try {
    const res = await fetch(`${API_BASE}/trips`, { headers: authHeaders() });
    if (!res.ok) return [];
    const json = await res.json();
    return json.data ?? [];
  } catch {
    return [];
  }
}

export async function apiCreateTrip(
  name: string,
  opts?: ApiTripOptions
): Promise<ApiTrip | null> {
  if (!isLoggedIn()) return null;
  try {
    const res = await fetch(`${API_BASE}/trips`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ name, ...opts }),
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json.data ?? null;
  } catch {
    return null;
  }
}

export async function apiUpdateTrip(
  tripId: string,
  name: string,
  opts?: ApiTripOptions
): Promise<boolean> {
  if (!isLoggedIn()) return false;
  try {
    const res = await fetch(`${API_BASE}/trips/${tripId}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ name, ...opts }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function apiDeleteTrip(tripId: string): Promise<boolean> {
  if (!isLoggedIn()) return false;
  try {
    const res = await fetch(`${API_BASE}/trips/${tripId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return res.ok;
  } catch {
    return false;
  }
}

// ─── Stops ─────────────────────────────────────────────────────────────────────

export async function apiAddStop(
  tripId: string,
  stop: {
    entity_id?: number;
    name: string;
    kind?: string;
    lat?: number;
    lng?: number;
    nights?: number;
    notes?: string;
    order_index?: number;
    locked?: boolean;
    tags?: string[];
    constraints?: string[];
  }
): Promise<ApiTripStop | null> {
  if (!isLoggedIn()) return null;
  try {
    const res = await fetch(`${API_BASE}/trips/${tripId}/stops`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ kind: 'camp', nights: 1, ...stop }),
    });
    if (!res.ok) return null;
    const json = await res.json();
    return json.data ?? null;
  } catch {
    return null;
  }
}

export async function apiUpdateStop(
  tripId: string,
  stopId: string,
  updates: Partial<Omit<ApiTripStop, 'id' | 'added_at'>>
): Promise<boolean> {
  if (!isLoggedIn()) return false;
  try {
    const res = await fetch(`${API_BASE}/trips/${tripId}/stops/${stopId}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(updates),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function apiDeleteStop(tripId: string, stopId: string): Promise<boolean> {
  if (!isLoggedIn()) return false;
  try {
    const res = await fetch(`${API_BASE}/trips/${tripId}/stops/${stopId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function apiReorderStops(tripId: string, stopIds: string[]): Promise<boolean> {
  if (!isLoggedIn()) return false;
  try {
    const res = await fetch(`${API_BASE}/trips/${tripId}/stops/reorder`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ stop_ids: stopIds }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

// ─── Export (GPX / KML) ─────────────────────────────────────────────────────

/**
 * Download the trip as a GPX or KML file (waypoints + a road-routed track).
 * Fetches with the user JWT (the endpoint is auth-gated, so a plain <a download>
 * won't work) and triggers a browser download. Returns false if not logged in
 * or the request fails.
 */
export async function apiExportTrip(
  tripId: string,
  fmt: 'gpx' | 'kml',
  opts?: { profile?: string; filename?: string }
): Promise<boolean> {
  if (!isLoggedIn()) return false;
  try {
    const qs = opts?.profile ? `?profile=${encodeURIComponent(opts.profile)}` : '';
    const res = await fetch(`${API_BASE}/trips/${tripId}/export.${fmt}${qs}`, {
      headers: authHeaders(),
    });
    if (!res.ok) return false;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = opts?.filename || `trip.${fmt}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return true;
  } catch {
    return false;
  }
}

// ─── Trip sheet + share links (flask-service/trip_share_routes.py) ───────────

export interface SheetFuelStop {
  name: string;
  brand?: string;
  lat: number;
  lng: number;
  along_km: number;
  off_track_km?: number;
}

export interface SheetLeg {
  from_stop_id: string;
  to_stop_id: string;
  distance_km: number | null;
  duration_minutes: number | null;
  routed: boolean;
  warnings: string[];
}

export interface SheetDrive extends SheetLeg {
  fuel_stops: SheetFuelStop[];
  fuel_gap: { gap_km: number; after_km: number } | null;
  /** The day's road, thinned ([lat, lng] pairs). Empty when not routed. */
  polyline?: [number, number][];
}

export interface SheetEntity {
  name?: string | null;
  booking_url?: string | null;
  image?: string | null;
  entity_type?: string | null;
  town?: string | null;
  state?: string | null;
}

export interface TripSheetData {
  vehicle_profile: string;
  legs: SheetLeg[];
  drives: SheetDrive[];
  entities: Record<string, SheetEntity>;
}

export interface ShareState {
  shared: boolean;
  token: string | null;
  share_url: string | null;
}

/** Shared (read-only) trip — no id, no owner. */
export interface SharedTrip {
  name: string;
  start_date?: string | null;
  end_date?: string | null;
  drive_hours_per_day?: number | null;
  vehicle_type?: string | null;
  stops: Omit<ApiTripStop, 'added_at' | 'locked' | 'constraints'>[];
  read_only: true;
  updated_at?: string | null;
  /** When the owner shared it (ISO). */
  shared_at?: string | null;
  /** How many people copied it with "Take this trip". */
  take_count?: number | null;
  /** The name the planner chose to share it under ("Planned by Sam"), if any. */
  shared_by?: string | null;
}

async function getJson<T>(url: string, init?: RequestInit): Promise<T | null> {
  try {
    const res = await fetch(url, init);
    if (!res.ok) return null;
    const json = await res.json();
    return (json?.data as T) ?? null;
  } catch {
    return null;
  }
}

/** Routed legs, per-day drives (with fuel) and campsite booking info. */
export async function apiGetTripSheet(
  tripId: string,
  vehicle?: string | null
): Promise<TripSheetData | null> {
  if (!isLoggedIn()) return null;
  const qs = vehicle ? `?vehicle=${encodeURIComponent(vehicle)}` : '';
  return getJson<TripSheetData>(`${API_BASE}/trips/${encodeURIComponent(tripId)}/sheet${qs}`, {
    headers: authHeaders(),
  });
}

export async function apiGetShareState(tripId: string): Promise<ShareState | null> {
  if (!isLoggedIn()) return null;
  return getJson<ShareState>(`${API_BASE}/trips/${encodeURIComponent(tripId)}/share`, {
    headers: authHeaders(),
  });
}

export async function apiShareTrip(tripId: string): Promise<ShareState | null> {
  if (!isLoggedIn()) return null;
  const data = await getJson<{ token: string; share_url: string }>(
    `${API_BASE}/trips/${encodeURIComponent(tripId)}/share`,
    { method: 'POST', headers: authHeaders() }
  );
  return data ? { shared: true, token: data.token, share_url: data.share_url } : null;
}

export async function apiRevokeShare(tripId: string): Promise<boolean> {
  if (!isLoggedIn()) return false;
  try {
    const res = await fetch(`${API_BASE}/trips/${encodeURIComponent(tripId)}/share`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Public — no token needed. Null when the link is unknown or turned off. */
export async function apiGetSharedTrip(token: string): Promise<SharedTrip | null> {
  return getJson<SharedTrip>(`${API_BASE}/trips/shared/${encodeURIComponent(token)}`);
}

/** Count one "Take this trip". Fire and forget; returns the new count or null. */
export async function apiTakeSharedTrip(token: string): Promise<number | null> {
  const data = await getJson<{ take_count: number }>(`${API_BASE}/trips/shared/${encodeURIComponent(token)}/take`, {
    method: 'POST',
    keepalive: true,
  });
  return typeof data?.take_count === 'number' ? data.take_count : null;
}

export type AdoptSharedResult =
  | { ok: true; id: string; name: string; already: boolean }
  | { ok: false; reason: 'signed-out' | 'gone' | 'quota' | 'failed' };

/**
 * Copy a shared trip into the signed-in user's account (it then shows up in
 * the app too). Taking a link you already hold returns that trip, not a copy.
 */
export async function apiAdoptSharedTrip(token: string): Promise<AdoptSharedResult> {
  if (!isLoggedIn()) return { ok: false, reason: 'signed-out' };
  try {
    const res = await fetch(`${API_BASE}/trips/adopt`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ share_token: token }),
    });
    if (res.status === 401) return { ok: false, reason: 'signed-out' };
    if (res.status === 404) return { ok: false, reason: 'gone' };
    if (res.status === 402) return { ok: false, reason: 'quota' };
    if (!res.ok) return { ok: false, reason: 'failed' };
    const data = (await res.json())?.data;
    if (!data?.id) return { ok: false, reason: 'failed' };
    return { ok: true, id: data.id, name: data.name || '', already: !!data.already };
  } catch {
    return { ok: false, reason: 'failed' };
  }
}

export async function apiGetSharedSheet(token: string): Promise<TripSheetData | null> {
  return getJson<TripSheetData>(`${API_BASE}/trips/shared/${encodeURIComponent(token)}/sheet`);
}

// ─── Saved campsites ──────────────────────────────────────────────────────────

export async function apiGetSavedCampsiteIds(): Promise<number[]> {
  if (!isLoggedIn()) return [];
  try {
    const res = await fetch(`${API_BASE}/auth/saved-campsites`, { headers: authHeaders() });
    if (!res.ok) return [];
    const json = await res.json();
    return (json.saved_campsites ?? []).map((item: any) => item.entity_id).filter(Boolean);
  } catch {
    return [];
  }
}

export async function apiSetSavedCampsite(entityId: number, saved: boolean): Promise<void> {
  if (!isLoggedIn()) return;
  try {
    await fetch(`${API_BASE}/auth/saved-campsites/${entityId}`, {
      method: saved ? 'PUT' : 'DELETE',
      headers: authHeaders(),
    });
  } catch {
    // swallow — UI already updated optimistically
  }
}
